import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { ref, get, push } from "firebase/database";
import { useAuth } from "../context/AuthContext";
import { GraduationCap, Plus, X, AlertCircle, Star, MessageCircle, ArrowLeft } from "lucide-react";

const SUBJECTS = [
  "Mathematics", "Physics", "Chemistry", "Programming / DSA", "DBMS",
  "Operating Systems", "Computer Networks", "Data Science / ML",
  "Electronics", "Mechanics", "Management", "English", "Other"
];

function TutorCard({ tutor }) {
  const waMsg = encodeURIComponent(
    `Hi! I found your tutoring profile on CampusSwap. I need help with ${tutor.subjects?.join(", ")}. Can we discuss further?`
  );
  return (
    <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
          <span className="text-pink-600 font-bold text-lg">{tutor.tutorName?.[0]?.toUpperCase()}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className="font-bold text-gray-800 text-sm">{tutor.tutorName}</p>
            <span className="text-blue-600 font-bold text-sm">₹{tutor.pricePerHour}/hr</span>
          </div>
          <p className="text-xs text-gray-500">{tutor.college} · Year {tutor.year}</p>
          <div className="flex flex-wrap gap-1 mt-1.5">
            {tutor.subjects?.map(s => (
              <span key={s} className="bg-pink-50 text-pink-700 text-xs px-2 py-0.5 rounded-full border border-pink-100">{s}</span>
            ))}
          </div>
        </div>
      </div>
      <div className="flex gap-3 mt-2 text-xs text-gray-500">
        <span className={`px-2 py-0.5 rounded-full font-medium ${tutor.mode === "Online" ? "bg-blue-50 text-blue-600" : tutor.mode === "Offline" ? "bg-green-50 text-green-600" : "bg-purple-50 text-purple-600"}`}>
          {tutor.mode}
        </span>
        {tutor.availability && <span className="text-gray-400">{tutor.availability}</span>}
      </div>
      {tutor.description && <p className="text-xs text-gray-500 mt-2 line-clamp-2">{tutor.description}</p>}
      <a href={`https://wa.me/91${tutor.whatsapp?.replace(/\D/g, "")}?text=${waMsg}`}
        target="_blank" rel="noreferrer"
        className="mt-3 flex items-center justify-center gap-2 w-full bg-green-500 text-white py-2 rounded-xl text-sm font-semibold hover:bg-green-600">
        <MessageCircle size={15} /> Contact Tutor
      </a>
    </div>
  );
}

export default function Tutoring() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [filterSubject, setFilterSubject] = useState("All");
  const [form, setForm] = useState({
    description: "", pricePerHour: "", mode: "Online", year: "", availability: "", whatsapp: userProfile?.phone || ""
  });

  useEffect(() => { fetchTutors(); }, []);

  async function fetchTutors() {
    setLoading(true);
    try {
      const snap = await get(ref(db, "tutoring"));
      if (snap.exists()) {
        const all = Object.entries(snap.val()).map(([id, data]) => ({ id, ...data }));
        setTutors(all.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      } else {
        setTutors([]);
      }
    } catch (e) {  }
    setLoading(false);
  }

  function toggleSubject(s) {
    setSelectedSubjects(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  }

  function handleChange(e) { setForm({ ...form, [e.target.name]: e.target.value }); }

  async function handlePost(e) {
    e.preventDefault();
    setError("");
    if (selectedSubjects.length === 0) return setError("Select at least one subject.");
    if (!form.pricePerHour) return setError("Enter your hourly rate.");
    if (!form.year) return setError("Enter your year of study.");
    if (!form.whatsapp.trim() || !/^[6-9]\d{9}$/.test(form.whatsapp.replace(/\s/g, "")))
      return setError("Enter a valid 10-digit WhatsApp number.");
    setPosting(true);
    try {
      await push(ref(db, "tutoring"), {
        subjects: selectedSubjects,
        description: form.description.trim(),
        pricePerHour: Number(form.pricePerHour),
        mode: form.mode,
        year: form.year,
        availability: form.availability.trim(),
        whatsapp: form.whatsapp.trim(),
        tutorId: currentUser.uid,
        tutorName: userProfile?.name || "Student",
        college: userProfile?.college || "",
        rating: 0,
        totalRatings: 0,
        createdAt: new Date().toISOString(),
      });
      setForm({ description: "", pricePerHour: "", mode: "Online", year: "", availability: "", whatsapp: userProfile?.phone || "" });
      setSelectedSubjects([]);
      setShowForm(false);
      fetchTutors();
    } catch (err) { setError("Failed to post. Try again."); }
    setPosting(false);
  }

  const filtered = filterSubject === "All" ? tutors : tutors.filter(t => t.subjects?.includes(filterSubject));

  return (
    <div className="pb-24 bg-gray-50 min-h-screen">
      <div className="bg-gradient-to-r from-pink-600 to-rose-600 px-4 pt-4 pb-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-1.5 -ml-1.5 rounded-full hover:bg-white/20"><ArrowLeft size={20} /></button>
            <div>
              <h1 className="text-xl font-bold">Peer Tutoring</h1>
              <p className="text-pink-100 text-xs mt-0.5">Learn from seniors · Teach juniors · Earn</p>
            </div>
          </div>
          <button onClick={() => setShowForm(true)}
            className="bg-white text-pink-700 px-3 py-1.5 rounded-xl font-semibold text-sm flex items-center gap-1">
            <Plus size={16} /> Offer Tutoring
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="px-4 -mt-3 mb-4">
        <div className="bg-white rounded-xl shadow-sm p-3">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {["All", ...SUBJECTS].map(s => (
              <button key={s} onClick={() => setFilterSubject(s)}
                className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium flex-shrink-0 ${filterSubject === s ? "bg-pink-500 text-white" : "bg-gray-100 text-gray-600"}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-4 space-y-3">
        {loading ? (
          [1,2,3].map(i => <div key={i} className="h-32 bg-white rounded-xl animate-pulse" />)
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl">
            <GraduationCap size={48} className="mx-auto mb-3 text-gray-200" />
            <p className="text-gray-500 font-medium">No tutors available yet</p>
            <button onClick={() => setShowForm(true)} className="mt-4 bg-pink-600 text-white px-6 py-2.5 rounded-xl font-semibold text-sm">
              Offer Tutoring
            </button>
          </div>
        ) : filtered.map(t => <TutorCard key={t.id} tutor={t} />)}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-end">
          <div className="bg-white rounded-t-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-4 py-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-gray-800 text-lg">Offer Tutoring</h3>
              <button onClick={() => { setShowForm(false); setError(""); }}><X size={24} /></button>
            </div>
            <form onSubmit={handlePost} className="p-4 space-y-3 pb-24">
              {error && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm"><AlertCircle size={16} />{error}</div>}

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Subjects I can teach *</p>
                <div className="flex flex-wrap gap-2">
                  {SUBJECTS.map(s => (
                    <button type="button" key={s} onClick={() => toggleSubject(s)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${selectedSubjects.includes(s) ? "bg-pink-500 text-white border-pink-500" : "bg-white text-gray-600 border-gray-200"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Year of Study *</label>
                  <select name="year" value={form.year} onChange={handleChange} required
                    className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm focus:ring-2 focus:ring-pink-400 text-gray-700">
                    <option value="">Select</option>
                    {["1st Year", "2nd Year", "3rd Year", "4th Year", "PG"].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Mode</label>
                  <select name="mode" value={form.mode} onChange={handleChange}
                    className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm focus:ring-2 focus:ring-pink-400 text-gray-700">
                    <option value="Online">Online</option>
                    <option value="Offline">Offline (On Campus)</option>
                    <option value="Both">Both</option>
                  </select>
                </div>
              </div>

              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">₹</span>
                <input name="pricePerHour" type="number" min="0" value={form.pricePerHour} onChange={handleChange} required
                  placeholder="Price per hour"
                  className="w-full border border-gray-200 rounded-xl pl-8 pr-4 py-3 text-sm focus:ring-2 focus:ring-pink-400" />
              </div>

              <input name="availability" value={form.availability} onChange={handleChange}
                placeholder="Availability (e.g. Weekdays 6-8 PM, Weekends)"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-pink-400" />

              <textarea name="description" value={form.description} onChange={handleChange} rows={2}
                placeholder="About yourself, teaching style, experience..."
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-pink-400 resize-none" />

              <input name="whatsapp" type="tel" value={form.whatsapp} onChange={handleChange} required
                placeholder="WhatsApp number *"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-pink-400" />

              <button type="submit" disabled={posting}
                className="w-full bg-pink-600 text-white py-3.5 rounded-xl font-semibold hover:bg-pink-700 disabled:opacity-50">
                {posting ? "Posting..." : "List as Tutor"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
